import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { environment } from '../../environments/environment';

export const REVELIO_IFRAME_READY = 'REVELIO_IFRAME_READY';
export const REVELIO_SESSION_TOKEN = 'REVELIO_SESSION_TOKEN';

/** How long to wait for parent postMessage before giving up. */
const POST_MESSAGE_TIMEOUT_MS = 5000;

type Result = {
  sessionToken: string | undefined;
  isAwaitingHandoff: boolean;
};

export const useSessionTokenHandoff = (): Result => {
  const urlToken = useUrlToken();

  const { postMessageToken, isAwaitingPostMessage } = usePostMessageToken({
    listenForPostMessage: !urlToken,
  });

  return {
    sessionToken: urlToken ?? postMessageToken,
    isAwaitingHandoff: isAwaitingPostMessage,
  };
};

const useUrlToken = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Capture URL token on first render.
  const [urlToken] = useState(
    () => searchParams.get('session_token') ?? undefined
  );

  // URL cleanup
  // biome-ignore lint/correctness/useExhaustiveDependencies: run once on mount
  useEffect(() => {
    if (!urlToken || !searchParams.has('session_token')) return;
    const next = new URLSearchParams(searchParams);
    next.delete('session_token');
    setSearchParams(next, { replace: true });
  }, []);

  return urlToken;
};

const usePostMessageToken = ({
  listenForPostMessage = false,
}: {
  listenForPostMessage: boolean;
}) => {
  const trustedOrigins = environment.SESSION_TOKEN_TRUSTED_ORIGINS ?? [];
  const isEmbedded =
    typeof window !== 'undefined' && window.top !== window.self;
  const shouldListenForPostMessage =
    listenForPostMessage && isEmbedded && trustedOrigins.length > 0;

  const [postMessageToken, setPostMessageToken] = useState<string | undefined>(
    undefined
  );
  const [isAwaitingPostMessage, setIsAwaitingPostMessage] = useState(
    shouldListenForPostMessage
  );

  // postMessage listener
  // biome-ignore lint/correctness/useExhaustiveDependencies: bound to mount only by design
  useEffect(() => {
    if (!shouldListenForPostMessage) return;

    const handleMessage = (event: MessageEvent) => {
      if (!trustedOrigins.includes(event.origin)) return;
      const data = event.data;
      if (
        !data ||
        typeof data !== 'object' ||
        data.type !== REVELIO_SESSION_TOKEN ||
        typeof data.token !== 'string' ||
        data.token.length === 0
      ) {
        return;
      }
      setPostMessageToken(data.token);
      setIsAwaitingPostMessage(false);
    };

    window.addEventListener('message', handleMessage);

    // Scheduled before postMessage so a throwing postMessage can never strand
    // us in the awaiting state with no escape hatch.
    const timeoutId = window.setTimeout(() => {
      setIsAwaitingPostMessage(false);
    }, POST_MESSAGE_TIMEOUT_MS);

    // Tell the parent we're listening. Targeting `'*'` is acceptable here
    // because the payload contains no sensitive data; the parent must
    // target a specific origin when sending the token in response.
    try {
      window.parent.postMessage({ type: REVELIO_IFRAME_READY }, '*');
    } catch {
      // Best-effort notification; the timeout above still clears the state.
    }

    return () => {
      window.removeEventListener('message', handleMessage);
      window.clearTimeout(timeoutId);
    };
  }, []);

  return { postMessageToken, isAwaitingPostMessage };
};
